import { StyledTableLink } from '@common/Dependency/styled';
import { statusToIndicator } from '@common/Execution';
import { Entity } from '@common/types/Entity';
import { getIDEUrl, Private_Routes } from '@common/url';
import { renderColumnWithEllipsis } from '@common/utils/renderUtils';
import { PipelineIcon, SearchSmIcon, TrashCIcon } from '@prophecy/icons';
import { theme } from '@prophecy/theme';
import { Button, ColumnsType, Ellipsis, Stack, StackItem, Table, Text, Tooltip } from '@prophecy/ui-v3';
import { formattedDateTime } from '@prophecy/utils/date';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';
import { format as timeAgoFormat } from 'timeago.js';

import { useRunsData } from '../../Workflow/AttachFabric/componentRunhook';
import { PipelineJobRunType } from '../../Workflow/AttachFabric/types';
import { StyledTableWithFooter } from '../config/styled';
import { formatCount } from '../util';
import { Statistics } from './Statistics';

type Props = {
  uid: string;
  entity: Entity;
  entityName?: string;
  projectName?: string;
  isSessionUsed: boolean;
};

const StyledStack = styled(Stack)`
  overflow: hidden;
`;

export default function DatasetRuns({ uid, entity, entityName, projectName, isSessionUsed }: Props) {
  const [runId, setRunId] = useState<string>();
  const {
    loadMoreData,
    connected,
    datasetRuns,
    runDataLoading,
    runDataLoadingMessage,
    runDetailLoading,
    getDatasetRunDetails,
    setDatasetRunDetail,
    handleDeleteRunID,
    deletingIdsStatus,
    errorMsg
  } = useRunsData({ entity, uid, isMetadataRun: true });
  const rows = useMemo(
    () => (datasetRuns?.rows || []).map((row, i) => ({ ...row, key: row.uid + i })),
    [datasetRuns?.rows]
  );

  const getStatsDetail = useCallback(
    (datasetRunId: string) => {
      getDatasetRunDetails(datasetRunId, uid);
      setRunId(datasetRunId);
    },
    [getDatasetRunDetails, uid]
  );

  const columns: ColumnsType<PipelineJobRunType> = useMemo(
    () => [
      {
        key: 'status',
        dataIndex: 'status',
        align: 'center',
        width: '30px',
        render: (status: string) => statusToIndicator(status)
      },
      {
        key: 'jobName',
        dataIndex: 'jobName',
        title: 'Trigger',
        sortable: true,
        render: (name: string, record) => {
          const runType = record.runType;
          if (!name) return renderColumnWithEllipsis(runType ? runType + ' Run' : '--');
          return (
            <StyledTableLink
              to={Private_Routes.Job.home.getUrl({ uid: record.jobUID })}
              data-test-id={`dataset-runs-job-link-${record.jobUID}-button`}>
              <Ellipsis tooltip={true}>
                {name} {runType && <span>( {runType + ' Run'} ) </span>}
              </Ellipsis>
            </StyledTableLink>
          );
        }
      },
      {
        key: 'name',
        dataIndex: 'pipelineName',
        title: 'Pipeline',
        sortable: true,
        render: (name: string, record) => {
          return (
            <StyledTableLink
              to={Private_Routes.Pipeline.home.getUrl({ uid: record.pipelineUID })}
              data-test-id={`dataset-runs-pipeline-link-${record.pipelineUID}-button`}>
              {renderColumnWithEllipsis(name)}
            </StyledTableLink>
          );
        }
      },
      {
        key: 'branch',
        dataIndex: 'branch',
        title: 'Branch',
        sortable: true,
        render: renderColumnWithEllipsis
      },
      {
        key: 'component',
        dataIndex: 'componentName',
        title: 'Gem',
        sortable: true,
        render: renderColumnWithEllipsis
      },
      {
        key: 'submissionTime',
        dataIndex: 'submissionTime',
        width: '20%',
        title: 'Start Time',
        sortable: true,
        render: (submissionTime: number) => {
          return (
            <Tooltip title={formattedDateTime(submissionTime)}>
              <span>{timeAgoFormat(submissionTime)}</span>
            </Tooltip>
          );
        }
      },
      {
        key: 'type',
        dataIndex: 'componentType',
        title: 'Usage',
        sortable: true
      },
      {
        key: 'record',
        dataIndex: 'recordsProcessed',
        title: 'Records',
        sortable: true,
        render: (record: number) => renderColumnWithEllipsis(formatCount(record))
      },
      {
        key: 'actions',
        width: '120px',
        align: 'center',
        dataIndex: 'actions',
        resizable: false,
        render: (_: string, record, i: number) => {
          const runId = record.uid;
          const isLoading = typeof deletingIdsStatus?.[runId] === 'number';
          return (
            <Tooltip title={connected ? null : 'Attach the cluster to perform any action.'}>
              <Stack direction='horizontal' gap={theme.spaces.x8}>
                <Table.IconButton
                  icon={<SearchSmIcon type='default' />}
                  disabled={!connected}
                  onClick={() => getStatsDetail(runId)}
                  aria-label='View run details'
                  data-test-id={`dataset-runs-search-${runId}-button`}
                />
                <Table.IconButton
                  icon={<PipelineIcon type='extended' />}
                  elementType='link'
                  to={getIDEUrl(
                    { uid: record.pipelineUID, entity: Entity.Pipeline },
                    { 'run-id': record.pipelineRun.uid }
                  )}
                  aria-label='Open pipeline'
                  data-test-id={`dataset-runs-pipeline-link-${runId}-button`}
                />
                <Table.IconButton
                  onClick={() => handleDeleteRunID(runId, uid, i)}
                  loading={isLoading}
                  disabled={isLoading || runDataLoading || !connected}
                  icon={isLoading ? null : <TrashCIcon type='default' size='xs' />}
                  aria-label='Delete run'
                  data-test-id={`dataset-runs-delete-${runId}-button`}
                />
              </Stack>
            </Tooltip>
          );
        }
      }
    ],
    [connected, getStatsDetail, deletingIdsStatus, runDataLoading, handleDeleteRunID, uid]
  );
  return (
    <StyledStack gap={theme.spaces.x40} height='100%' align='space-between'>
      {runId && (
        <Statistics
          entity={entity}
          runId={runId}
          loading={runDetailLoading && connected}
          uid={uid}
          getDatasetRunDetails={getDatasetRunDetails}
          entityName={entityName}
          projectName={projectName}
          onClose={() => {
            setRunId(undefined);
            setDatasetRunDetail(undefined);
          }}
        />
      )}
      <Stack direction='horizontal' gap={theme.spaces.x12} align='space-between' alignY='center'>
        <Text level='md'>Historical Runs</Text>
      </Stack>

      <StackItem grow='1'>
        <StyledTableWithFooter
          data-test-id='dataset-runs-table'
          columns={columns}
          height='100%'
          showEmptyMessage={{
            description: connected
              ? runDataLoading
                ? runDataLoadingMessage
                : errorMsg || 'No Data'
              : isSessionUsed
                ? 'Recent session connected is already in use. Use another session to connect.'
                : errorMsg || 'Attach cluster to see the dataset run details'
          }}
          footer={() =>
            datasetRuns.next_filters ? (
              <Button
                size='s'
                variant='secondaryGray'
                disabled={runDataLoading}
                loading={runDataLoading && connected}
                onClick={() => loadMoreData()}
                data-test-id='dataset-runs-view-more-button'>
                View More
              </Button>
            ) : null
          }
          dataSource={rows}
          fixedHeader={true}
          virtualize={true}
        />
      </StackItem>
    </StyledStack>
  );
}
